<template>
  <div class="detail-header">
    <div class="detail-header__color-run">
      <StitchColorRun :images="itemColorRun" />
    </div>
    <div class="detail-header__info">
      <div class="detail-header__title">
        <div class="detail-header__title-detail">
          <StitchTooltip :message="item.name">
            <h1>
              {{ truncateText(item.name, 25) }}
            </h1>
          </StitchTooltip>
          <StitchTooltip
            v-if="libraryType === LIBRARY_TYPE.STYLE"
            :message="`QC - ${qualityCheckTooltip}`"
            placement="right"
          >
            <span class="detail-header__title-quality-check">
              <i
                v-if="styleQualityCheckStatus === null"
                class="el-icon-remove-outline not-set"
              />
              <i
                v-else-if="styleQualityCheckStatus"
                class="el-icon-success approved"
              />
              <i
                v-else
                class="el-icon-error rejected"
              />
            </span>
          </StitchTooltip>
          <StitchTooltip
            v-if="libraryType === LIBRARY_TYPE.STYLE"
            :message="watchState ? `Remove from Watchlist` : `Add to Watchlist`"
            placement="right"
          >
            <span
              class="detail-header__title-watchlist"
              @click="toggleWatchState"
            >
              <StitchLoader
                v-if="isTogglingWatch"
                size="mini"
                class="detail-header__title-watchlist-loading"
              />
              <span v-else>
                <i
                  v-if="!watchState"
                  class="el-icon-star-off not-set"
                />
                <i
                  v-else
                  class="el-icon-star-on watched"
                />
              </span>
            </span>
          </StitchTooltip>
        </div>
        <div class="detail-header__actions">
          <StyleComments
            v-if="item && libraryType === LIBRARY_TYPE.STYLE"
            :item="item"
          />

          <StitchTooltip message="Actions">
            <StitchDropdown
              v-if="!isVendorUser"
              :dropdown-items="criticalStatusTransitions"
              :append-to-body="false"
              class="detail-header__dropdown"
              :icon-type="ICON_TYPE.MENU"
            />
          </StitchTooltip>

          <!-- Style Status Action form -->
          <FormGenerator
            :show-form-generator="showStyleStatusActionForm"
            :form-configuration="styleStatusActionFormConfiguration"
            :additional-data="item"
            :original-data="actionConfirmationFormData"
            :default-data="styleStatusActionDefaultFormData"
            :options="styleStatusActionReasons"
            :is-creation="true"
            @form-close="onStyleStatusActionFormClose"
            @form-complete="completeStyleStatusActionForm"
          />
        </div>
      </div>
      <table class="detail-header__table">
        <tr v-if="item.plmCode">
          <td>PLM Code</td>
          <td>{{ item.plmCode }}</td>
        </tr>
        <tr>
          <td>Info</td>
          <td>
            <div
              v-if="visibleTags.length > 0"
              class="detail-header__meta"
            >
              <ElementTag
                v-for="(value, index) in visibleTags"
                :key="index"
                size="mini"
                type="info"
              >
                {{ value }}
              </ElementTag>

              <ElementButton
                v-if="itemMeta.length > maxVisibleTags"
                type="text"
                @click="toggleShowAllTags"
              >
                {{ showAllTags ? '- See less' : '+ See more' }}
              </ElementButton>
            </div>
            <template v-else>
              No info available
            </template>
          </td>
        </tr>
        <tr>
          <td>{{ item.contributor ? 'Created by' : 'Created on' }}</td>
          <td>{{ createdInfoFormatted }}</td>
        </tr>
        <tr v-if="libraryType === LIBRARY_TYPE.STYLE">
          <td>Responsible Product</td>
          <td>
            <template v-if="isVendorUser">
              {{ item.responsibleProduct }}
            </template>
            <template v-else-if="!editableResponsibleProduct.edit">
              {{ item.responsibleProduct }}
              <i
                class="detail-header__button-actions el-icon-edit"
                @click="cancelEditingResponsibleProduct"
              />
            </template>
            <template v-else>
              <ElementSelect
                v-model="editableResponsibleProduct.id"
                placeholder="Responsible Product"
                filterable
                clearable
              >
                <ElementOption
                  v-for="rp in responsibleProductUsers"
                  :key="rp.id"
                  :label="rp.email"
                  :value="rp.id"
                />
              </ElementSelect>
              <i
                class="detail-header__button-actions el-icon-check"
                @click="saveResponsibleProduct"
              />
              <i
                class="detail-header__button-actions el-icon-close"
                @click="cancelEditingResponsibleProduct"
              />
            </template>
          </td>
        </tr>
        <tr v-if="libraryType === LIBRARY_TYPE.STYLE">
          <td>Submission Deadline</td>
          <td>
            <template v-if="!editableSubmissionDeadline.edit">
              <template
                v-if="
                  isOpenStatus &&
                    item.submissionDeadline.days !== null &&
                    item.submissionDeadline.days <= 7
                "
              >
                <span
                  :class="[
                    !item.submissionDeadline.is_passed ? 'close' : 'passed'
                  ]"
                >
                  {{ submissionDeadlineDate }}
                </span>
              </template>
              <span v-else>
                {{ submissionDeadlineDate }}
              </span>
              <i
                v-if="!isVendorUser"
                class="detail-header__button-actions el-icon-edit"
                @click="cancelEditingSubmissionDeadline"
              />
            </template>
            <template v-else>
              <ElementDatePicker
                v-model="editableSubmissionDeadline.deadline"
                type="date"
                placeholder="Submission Deadline"
                :picker-options="datePickerOptions"
                format="dd-MM-yyyy"
                value-format="yyyy-MM-dd"
                class="form-generator__content-item-date-picker"
              />
              <i
                class="detail-header__button-actions el-icon-check"
                @click="saveSubmissionDeadline"
              />
              <i
                class="detail-header__button-actions el-icon-close"
                @click="cancelEditingSubmissionDeadline"
              />
            </template>
          </td>
        </tr>
        <template v-if="libraryType === LIBRARY_TYPE.STYLE">
          <tr v-if="!isVendorUser">
            <td>Assigned Vendor Company</td>
            <td>
              <template v-if="isLoadingVendor">
                <StitchLoader
                  size="mini"
                  placement="start"
                />
              </template>
              <template v-else>
                <VendorSelector
                  :vendors="vendors"
                  :vendor-company-id="item.vendorCompanyId"
                  @update-vendor="updateStyleVendor"
                />
              </template>
            </td>
          </tr>
          <tr v-if="!isVendorUser && isAdvancedUser">
            <td>Quality Check</td>
            <td>
              <ElementSwitch
                v-model="styleQualityCheckStatus"
                :disabled="isLoadingQualityCheck"
                :class="{
                  'detail-header__qc-neutral-state':
                    styleQualityCheckStatus === null
                }"
                :active-color="styleQCActiveColor"
                :inactive-color="styleQCInactiveColor"
                @change="onQualityCheckChange"
              />
              <template v-if="!isLoadingQualityCheck">
                <ElementButton
                  v-if="styleQualityCheckStatus !== null"
                  class="detail-header__qc-reset"
                  type="text"
                  title="Reset Quality Check to Not Checked"
                  @click="onQualityCheckReset"
                >
                  <i class="el-icon-refresh-left" />
                </ElementButton>
                <span>
                  {{ qualityCheckTooltip }}
                </span>
                <ElementTooltip
                  v-if="item.qualityCheckChangeLog"
                  effect="dark"
                  :content="`Changed by ${
                    item.qualityCheckChangeLog.changed_by
                  },
                  ${formatToLocaleDateShort(
                    item.qualityCheckChangeLog.changed_at
                  )}`"
                  placement="right-start"
                  :open-delay="400"
                  :enterable="false"
                >
                  <i
                    id="library-status--log-tooltip"
                    class="el-icon-info"
                  />
                </ElementTooltip>
              </template>
            </td>
          </tr>
          <tr>
            <td>Status</td>
            <td>
              <template v-if="isLoadingStatus">
                <StitchLoader
                  size="mini"
                  placement="start"
                />
              </template>
              <template v-else>
                <LibraryStatusSelector
                  :item="item"
                  :transitions="directStatusTransitions"
                  @update-status="updateStatusWithoutConfirmation"
                />
              </template>
            </td>
          </tr>
        </template>
      </table>
      <div class="detail-header__actions">
        <StitchDropdown
          :dropdown-items="dropdownItems"
          :append-to-body="false"
          class="detail-header__dropdown"
          :title="`Edit ${libraryType} information`"
          :is-disabled="isVendorUser"
          :disabled-message="disabledMessage"
        />
      </div>
    </div>
  </div>
</template>

<script>
import FeatureFlags from '@/services/featureFlags'
import { mapActions, mapGetters } from 'vuex'
import { DataUtils } from '@/mixins/utils.js'
import {
  formatToLocaleDate,
  formatToLocaleDateShort,
  truncateText
} from '@/services/utils'
import { getFormConfig } from '@/services/formUtils'
import StitchTooltip from '@/components/common/StitchTooltip'
import { IMAGE_RESOLUTION } from '@/constants/image'
import { LIBRARY_TYPE } from '@/constants/libraryType'
import { ItemShape } from '@/types'
import LibraryStatusSelector from '@/components/library/LibraryStatusSelector.vue'
import VendorSelector from '@/components/library/VendorSelector.vue'
import { ICON_TYPE, STYLE_TYPE } from '@/constants/dropdownOptions'
import {
  ASSET_STATUS,
  REASONS_FOR_CANCELLATION,
  REASONS_FOR_RESUBMITTING,
  CRITICAL_STATUSES,
  CRITICAL_STATUSES_NO_VALIDATION
} from '@/constants/assetStatuses'
import { FORM_NAME } from '@/constants/formName'
import { USER_ROLE } from '@/constants/roleType'

export default {
  name: 'DetailHeader',

  components: {
    StitchTooltip,
    LibraryStatusSelector,
    VendorSelector
  },

  mixins: [DataUtils],

  props: {
    item: ItemShape.loose.isRequired
  },

  emits: ['edit-item', 'delete-item', 'asset-status-action'],

  data () {
    return {
      LIBRARY_TYPE,
      ICON_TYPE,
      ASSET_STATUS,
      USER_ROLE,
      assetStatus: null,
      watchState: false,
      isTogglingWatch: false,
      isLoadingStatus: false,
      isLoadingVendor: false,
      isLoadingQualityCheck: false,
      showStyleStatusActionForm: false,
      styleStatusActionDefaultFormData: null,
      styleStatusActionReasons: {},
      styleQCActiveColor: '#69db8d',
      styleQCInactiveColor: '#f56c6c',
      styleQualityCheckStatus: null,
      disabledMessage:
        'As a vendor user, you are not allowed to perform this action.',
      editableResponsibleProduct: {
        id: null,
        email: null,
        edit: false
      },
      editableSubmissionDeadline: {
        deadline: null,
        edit: false
      },
      datePickerOptions: {
        /**
         * Disable any date before tomorrow.
         *
         * @param   {Date}    date - The date being evaluated by the picker.
         * @returns {boolean}      - `true` if the date should be disabled (i.e. is before tomorrow).
         */
        disabledDate (date) {
          const today = new Date()
          const tomorrow = new Date(
            today.getFullYear(),
            today.getMonth(),
            today.getDate() + 1
          )

          return date < tomorrow
        }
      },
      showAllTags: false,
      maxVisibleTags: 7
    }
  },

  computed: {
    ...mapGetters([
      'getItemStatus',
      'getStyleQualityStatus',
      'getStyleResponsibleProduct'
    ]),

    ...mapGetters({
      vendors: 'getVendors',
      user: 'getCognitoUserData',
      responsibleProductUsers: 'getResponsibleProductUsers'
    }),

    /**
     * @returns {boolean}
     */
    visibleTags () {
      return this.showAllTags
        ? this.itemMeta
        : this.itemMeta.slice(0, this.maxVisibleTags)
    },

    /**
     * @returns {boolean}
     */
    isOpenStatus () {
      return ![
        ASSET_STATUS.REVIEW.label,
        ASSET_STATUS.APPROVED.label,
        ASSET_STATUS.CANCELLED.label,
        ASSET_STATUS.PENDING.label,
        ASSET_STATUS.APPROVED_FOR_DAM.label
      ].includes(this.item.assetStatus)
    },

    /**
     * @returns {string}
     */
    submissionDeadlineDate () {
      return formatToLocaleDate(this.item.submissionDeadline.deadline)
    },

    /**
     * @returns {object}
     */
    itemDetail () {
      return this.getItemStatus()
    },

    /**
     * @returns {object}
     */
    qualityCheck () {
      if (this.item !== null && this.libraryType === LIBRARY_TYPE.STYLE) {
        return this.getStyleQualityStatus({ styleId: this.item.id })
      }

      return null
    },

    /**
     * @returns {string}
     */
    qualityCheckTooltip () {
      return this.styleQualityCheckStatus === null
        ? 'Not Set'
        : this.styleQualityCheckStatus
          ? 'Passed'
          : 'Needs Improvements'
    },

    /**
     * @returns {boolean}
     */
    isVendorUser () {
      return this.user.role === USER_ROLE.VENDOR.name
    },

    /**
     * @returns {boolean}
     */
    isAdvancedUser () {
      return FeatureFlags.isAdvancedUser()
    },

    /**
     * @returns {Array}
     */
    directStatusTransitions () {
      if (!this.itemDetail) {
        return []
      }

      return this.itemDetail.transitions.filter(
        transition =>
          !CRITICAL_STATUSES.includes(transition.value) &&
          transition.label !== this.item.assetStatus
      )
    },

    /**
     * @returns {Array}
     */
    criticalStatusTransitions () {
      if (!this.itemDetail) {
        return []
      }

      return this.itemDetail.transitions
        .filter(transition => CRITICAL_STATUSES.includes(transition.value))
        .map(transition => {
          return {
            name: transition.action,
            disabled: !transition.is_possible,
            method: () =>
              CRITICAL_STATUSES_NO_VALIDATION.includes(transition.value)
                ? this.updateStatusWithoutConfirmation(transition.value)
                : this.openAdvancedTransitionAction(transition),
            // Make sure to not display a message when status is already selected
            ...(transition.label !== this.item.assetStatus
              ? {
                  disabledMessage: [
                    `You cannot change status from ${this.item.assetStatus} to ${transition.label}.`
                  ]
                }
              : {})
          }
        })
    },

    /**
     * @returns {Array}
     */
    dropdownItems () {
      return [
        {
          name: 'Edit Information',
          method: () => this.emitEditItem()
        },
        {
          name: `Delete ${this.libraryType}`,
          styles: [STYLE_TYPE.DANGER],
          method: () => this.emitDeleteItem()
        }
      ]
    },

    /**
     * @returns {object}
     */
    itemMeta () {
      return this.item.meta
    },

    /**
     * @returns {object}
     */
    itemColorRun () {
      return this.getColorRunResized(this.item, IMAGE_RESOLUTION.THUMBNAIL)
    },

    /**
     * @returns {string}
     */
    libraryType () {
      return this.item.libraryType
    },

    /**
     * @returns {string}
     */
    createdInfoFormatted () {
      if (this.item.contributor) {
        return `${this.item.contributor} on ${formatToLocaleDateShort(
          this.item.createdAt
        )}`
      }

      return formatToLocaleDateShort(this.item.createdAt)
    },

    /**
     * @returns {object}
     */
    styleStatusActionFormConfiguration () {
      return getFormConfig(FORM_NAME.STATUS_CHANGE_CONFIRMATION)
    },

    /**
     * @returns {object}
     */
    actionConfirmationFormData () {
      return {
        name: this.item.name,
        plmCode: this.item.plmCode
      }
    }
  },

  /**
   * Vue watcher that syncs the `watchState` data property
   * with the `item.watched` property.
   *
   * This ensures that when the parent-provided `item.watched` changes,
   * the internal `watchState` stays up to date.
   *
   * @param {boolean} val - The new value of `item.watched`
   */
  watch: {
    'item.watched': {
      immediate: true,
      /**
       * Handler for changes to item.watched
       *
       * @param {boolean} val - New watched state from item
       */
      handler (val) {
        this.watchState = val
      }
    }
  },

  async created () {
    if (this.item !== null && this.libraryType === LIBRARY_TYPE.STYLE) {
      await this.initStatuses()
      this.styleQualityCheckStatus = this.qualityCheck

      if (!this.isVendorUser) {
        await this.fetchAssignableVendorsByTeam({ teamId: this.item.groupId })
        await this.fetchResponsibleProductUsers({
          teamId: this.item.groupId
        })

        if (this.item.submissionDeadline) {
          this.editableSubmissionDeadline.deadline =
            this.item.submissionDeadline.deadline
        }
      }

      if (this.item && typeof this.item.watched === 'boolean') {
        this.watchState = this.item.watched
      }
    }
  },

  beforeDestroy () {
    this.clearItemStatus({ libraryType: this.libraryType })
  },

  methods: {
    truncateText,

    formatToLocaleDateShort,

    ...mapActions([
      'fetchItemAssetStatus',
      'fetchAssignableVendorsByTeam',
      'updateAssetStatus',
      'toggleWatchStyle',
      'clearItemStatus',
      'updateVendor',
      'updateQualityCheckStatus',
      'fetchResponsibleProductUsers',
      'updateResponsibleProduct',
      'updateSubmissionDeadline'
    ]),

    /**
     *
     */
    toggleShowAllTags () {
      this.showAllTags = !this.showAllTags
    },

    /**
     *
     */
    async saveResponsibleProduct () {
      if (this.editableResponsibleProduct.id) {
        await this.updateResponsibleProduct({
          styleId: this.item.id,
          responsibleProductId: this.editableResponsibleProduct.id
        })

        this.editableResponsibleProduct = {
          id: null,
          email: null,
          edit: false
        }
      }
    },

    /**
     *
     */
    cancelEditingResponsibleProduct () {
      this.editableResponsibleProduct.edit =
        !this.editableResponsibleProduct.edit
    },

    /**
     *
     */
    async saveSubmissionDeadline () {
      if (this.editableSubmissionDeadline.deadline) {
        await this.updateSubmissionDeadline({
          styleId: this.item.id,
          deadline: this.editableSubmissionDeadline.deadline
        })

        this.editableSubmissionDeadline = {
          deadline: null,
          edit: false
        }
      }
    },

    /**
     *
     */
    cancelEditingSubmissionDeadline () {
      this.editableSubmissionDeadline.edit =
        !this.editableSubmissionDeadline.edit
    },

    /**
     *
     */
    async initStatuses () {
      this.isLoadingStatus = true

      this.assetStatus = await this.fetchItemAssetStatus({
        itemId: this.item.id,
        libraryType: this.libraryType
      })

      this.isLoadingStatus = false
    },

    /**
     * Toggles the "watched" state of the current style.
     *
     * @returns {Promise<void>}
     */
    async toggleWatchState () {
      this.isTogglingWatch = true
      const messageDuration = 5000

      try {
        const updated = await this.toggleWatchStyle({
          styleId: this.item.id,
          watched: !this.watchState
        })

        this.watchState = updated

        if (this.watchState) {
          this.$message({
            message: 'You have added this style to your watch list.',
            type: 'info',
            showClose: true,
            duration: messageDuration
          })
        } else {
          this.$message({
            message: 'You have removed this style from your watch list.',
            type: 'warning',
            showClose: true,
            duration: messageDuration
          })
        }
      } catch (error) {
        this.$message({
          message: 'Failed to update watch state.',
          type: 'error',
          showClose: true,
          duration: messageDuration
        })
      } finally {
        this.isTogglingWatch = false
      }
    },

    /**
     * @param {string} status
     */
    async updateStatusWithoutConfirmation (status) {
      this.isLoadingStatus = true

      await this.updateAssetStatus({
        id: this.item.id,
        assetStatus: status,
        isCritical: false
      })

      this.isLoadingStatus = false
    },

    /**
     * @param {number} vendorId
     */
    async updateStyleVendor (vendorId) {
      this.isLoadingVendor = true

      await this.updateVendor({
        libraryType: this.libraryType,
        itemId: this.item.id,
        vendorId: vendorId
      })

      this.isLoadingVendor = false
    },

    /**
     * @param {object} transition
     */
    openAdvancedTransitionAction (transition) {
      this.styleStatusActionDefaultFormData = {
        assetStatus: transition.label
      }

      this.styleStatusActionReasons = { changeReasons: [] }

      if (transition.label === ASSET_STATUS.CANCELLED.label) {
        this.styleStatusActionReasons.changeReasons = REASONS_FOR_CANCELLATION
      } else if (transition.label === ASSET_STATUS.RESUBMIT.label) {
        this.styleStatusActionReasons.changeReasons = REASONS_FOR_RESUBMITTING
      }

      this.showStyleStatusActionForm = true
    },

    /**
     * @param {boolean} value
     */
    async onQualityCheckChange (value) {
      this.isLoadingQualityCheck = true

      if (this.styleQualityCheckStatus === null) {
        this.styleQualityCheckStatus = true
      } else {
        this.styleQualityCheckStatus = value
      }

      await this.updateQualityCheckStatus({
        styleId: this.item.id,
        status: this.styleQualityCheckStatus
      })

      this.isLoadingQualityCheck = false
    },

    /**
     */
    async onQualityCheckReset () {
      this.isLoadingQualityCheck = true

      this.styleQualityCheckStatus = null

      await this.updateQualityCheckStatus({
        styleId: this.item.id,
        status: null
      })

      this.isLoadingQualityCheck = false
    },

    /**
     */
    emitEditItem () {
      this.$emit('edit-item', { styleId: this.item.id })
    },

    /**
     * @returns {Promise<MessageBoxData>}
     */
    emitDeleteItem () {
      return this.$confirm(
        `This will permanently delete ${this.item.name}. Continue?`,
        'Warning',
        {
          confirmButtonText: 'OK',
          cancelButtonText: 'Cancel',
          type: 'warning',
          showClose: false
        }
      )
        .then(() => {
          this.$emit('delete-item', {
            id: this.item.id,
            name: this.item.name
          })
        })
        .catch(() => {})
    },

    /**
     *
     */
    onStyleStatusActionFormClose () {
      this.showStyleStatusActionForm = false
    },

    /**
     * @param {number} success
     */
    async completeStyleStatusActionForm (success) {
      if (success) {
        await this.initStatuses()
        this.onStyleStatusActionFormClose()
      }
    }
  }
}
</script>

<style lang="scss" scoped>
$meta-margin-left: spacing(1/2) * -1;
$meta-margin-bottom: -1 * spacing(2); // Hides the bottom padding from the last row of wrapped tags, making the whole table look cleaner.
$color-run-width: 40%;
$color-run-height: spacing(24);
$meta-list-name-width: 20%;
$meta-list-value-width: 80%;
$icon-size: 50px;

.detail-header {
  display: flex;
  width: 100%;
  margin-bottom: 0;
}

.detail-header__color-run {
  @include flex-center;

  width: $color-run-width;
  min-width: spacing(30);
  max-width: spacing(60);
  padding: spacing(2);

  > * {
    max-height: $color-run-height;
  }
}

.detail-header__info {
  display: flex;
  flex: 1;
  flex-direction: column;
  width: 100%;
  padding: spacing(2);
  overflow: hidden;
}

.detail-header__title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.detail-header__title-detail {
  display: flex;
  align-items: center;

  /deep/ .el-tooltip {
    display: inline-flex;
    margin-right: spacing(1);
  }

  h1 {
    display: flex;
    align-items: center;
    margin: 0;
    padding: 0;
    line-height: 1.2;
  }
}

.detail-header__title-quality-check {
  font-size: spacing(3.5);

  .approved {
    color: $status-approved;
  }

  .rejected {
    color: $red;
  }

  .not-set {
    color: $grey-light;
  }
}

.detail-header__title-watchlist {
  font-size: spacing(3.5);
  transition: transform 0.2s ease;

  .watched {
    color: $blue;
  }

  .not-set {
    color: $grey-light;
  }
}

.detail-header__title-watchlist:hover {
  transform: scale(1.2);
  cursor: pointer;
}

.detail-header__title-watchlist-loading {
  padding-top: spacing(1.5);
}

.detail-header__button-actions {
  padding-left: spacing(1);
  font-size: spacing(1.8);
  cursor: pointer;
  transition: $transition;
}

.detail-header__button-actions:hover {
  font-size: spacing(2);
}

.detail-header__meta {
  margin-bottom: $meta-margin-bottom;
  margin-left: $meta-margin-left;

  > * {
    margin: 0 spacing(1/2) spacing(2);
  }
}

.detail-header__dropdown {
  display: inline-flex;
  margin-bottom: spacing(2);

  /deep/ .el-button {
    padding: 0;
  }
}

.detail-header__actions {
  display: flex;
  align-items: center;
  margin-right: spacing(2);

  > * {
    margin-right: spacing(2);
  }
}

.detail-header__quality-check {
  display: inline-flex;
  padding-top: 0;

  i {
    font-size: spacing(2.5);
  }
}

.detail-header__table {
  width: 100%;
  margin-bottom: spacing(1);

  tr {
    display: flex;
    margin-bottom: spacing(1);
  }

  td {
    padding: spacing(1) 0;
  }

  td + td {
    margin-left: spacing(1);
  }

  td:first-of-type {
    width: $meta-list-name-width;
    color: $grey;
  }

  td:last-of-type {
    width: $meta-list-value-width;
    color: $grey-light;
  }
}

.detail-header__comments {
  /deep/ .el-button {
    padding: 0;
  }
}

.detail-header__qc-reset {
  width: spacing(3);
  padding: 0;
}

.close {
  color: $orange;
}

.passed {
  color: $red;
}
</style>

<style lang="scss">
.detail-header__qc-neutral-state .el-switch__core {
  background-color: $grey-very-light !important;
  border-color: $grey-very-light !important;
}
</style>
